const USERBACK_SCRIPT_URL = 'https://static.userback.io/widget/v1.js';
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 2000; // 2 seconds

// Module-level state
let _pendingOffset = '20px';
let _widgetHostEl = null;
let _styleObserver = null;
let _bodyObserver = null;
let _retryTimer = null;
let _applyingOffset = false; // Guard against observer re-entrancy

/**
 * Find the UBDIV host element the Userback widget injects.
 */
function findWidgetHost() {
  if (_widgetHostEl && document.body.contains(_widgetHostEl)) return _widgetHostEl;
  _widgetHostEl = document.getElementById('userback_button_container');
  return _widgetHostEl;
}

/**
 * Apply the current bottom offset to the host element only.
 * Do NOT touch shadow root internals or children — that causes duplicate icons.
 */
function applyOffset() {
  if (_applyingOffset) return; // Prevent observer loop
  _applyingOffset = true;

  const el = findWidgetHost();
  if (el) {
    el.style.setProperty('bottom', _pendingOffset, 'important');
    el.style.setProperty('top', 'auto', 'important');
    el.style.setProperty('transition', 'bottom 0.25s ease');
  }

  _applyingOffset = false;
}

/**
 * Watch the host element's style attribute. If the widget's own JS resets
 * the inline bottom value, we immediately re-apply ours.
 * Only watches the style attribute — NOT childList/subtree — to avoid
 * interfering with the widget's internal DOM.
 */
function startStyleGuard(el) {
  if (_styleObserver) _styleObserver.disconnect();
  _styleObserver = new MutationObserver(() => {
    applyOffset();
  });
  _styleObserver.observe(el, {
    attributes: true,
    attributeFilter: ['style'],
  });
}

/**
 * Watch for the widget to appear in the DOM.
 */
function watchForWidget() {
  if (_bodyObserver) _bodyObserver.disconnect();
  _bodyObserver = new MutationObserver(() => {
    const el = findWidgetHost();
    if (el) {
      _bodyObserver.disconnect();
      applyOffset();
      startStyleGuard(el);
    }
  });
  _bodyObserver.observe(document.body, { childList: true, subtree: false });
}

/**
 * Stop capture and remove the SDK when the authenticated user changes or logs
 * out so feedback identity cannot leak between sessions.
 */
export const disableUserback = () => {
  if (_retryTimer) {
    clearTimeout(_retryTimer);
    _retryTimer = null;
  }
  if (_styleObserver) _styleObserver.disconnect();
  if (_bodyObserver) _bodyObserver.disconnect();

  try {
    window.Userback?.stopSessionReplay?.();
    window.Userback?.destroy?.();
  } catch (error) {
    console.warn('Unable to fully stop Userback:', error);
  }

  document.querySelectorAll(`script[src="${USERBACK_SCRIPT_URL}"]`).forEach((script) => script.remove());
  document.getElementById('userback_button_container')?.remove();
  _widgetHostEl = null;
  window.Userback = undefined;
};

/**
 * Initialise the Userback feedback widget.
 *
 * @param {object} user     Firebase Auth user (uid, email, displayName)
 * @param {object} [profile] Optional Firestore profile — role, company, billing
 * @param {number} [retryCount] Internal retry counter
 */
export const initializeUserback = (user, profile = {}, retryCount = 0) => {
  if (window.Userback?.widget_loaded) {
    return; // Already fully loaded
  }

  window.Userback = window.Userback || {};
  window.Userback.access_token = 'A-y2Jmvx5Jr2r08UVMDMajgVUtF';

  window.Userback.widget_settings = {
    position: 'se',
    style: 'circle',
  };

  // on_load fires after the widget UI renders
  window.Userback.on_load = () => {
    applyOffset();
    // Re-apply with delays in case widget finishes setup asynchronously
    setTimeout(applyOffset, 200);
    setTimeout(applyOffset, 1000);
    // Start the style guard after widget is fully loaded
    const el = findWidgetHost();
    if (el) startStyleGuard(el);
  };

  if (user) {
    window.Userback.user_data = {
      id: user.uid || user.id,
      info: {
        name: user.displayName || user.name || 'User',
        email: user.email
      },
      custom_data: {
        role: profile.userRole || 'unknown',
        company_id: profile.companyId || '',
        company_name: profile.companyName || '',
        subscription_status: profile.subscriptionStatus || '',
        plan: profile.stripePlan || '',
      }
    };
  }

  // Remove any existing failed script
  const existingScript = document.querySelector(`script[src="${USERBACK_SCRIPT_URL}"]`);
  if (existingScript) {
    existingScript.remove();
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = USERBACK_SCRIPT_URL;

  script.onload = () => {
    window.Userback.widget_loaded = true;
  };

  script.onerror = () => {
    if (retryCount < MAX_RETRIES) {
      const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
      console.warn(`Userback script failed to load, retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
      _retryTimer = setTimeout(() => {
        initializeUserback(user, profile, retryCount + 1);
      }, delay);
    } else {
      console.error('Userback script failed to load after max retries');
    }
  };

  // Start watching for widget insertion
  watchForWidget();

  (document.head || document.body).appendChild(script);
};

/**
 * Nudge the Userback widget button upward when the Activity Bar is at the
 * bottom of the screen so it doesn't overlap.
 *
 * Size tiers map to the activity bar CSS max-heights plus a buffer:
 *   Normal → offset 280px (constrained activity bar height)
 *
 * @param {'top' | 'bottom'} activityBarPosition
 * @param {boolean} activityBarOpen
 */
export const setUserbackPosition = (activityBarPosition, activityBarOpen) => {
  if (activityBarPosition !== 'bottom' || !activityBarOpen) {
    _pendingOffset = '20px';
  } else {
    // Activity bar at bottom — push widget above it
    _pendingOffset = '280px';
  }

  applyOffset();
};
